import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdoUserProfile } from '../../models/ado-user-profile';
import { UserSettings } from '../../models/user-settings';
import { GitHubConnectionInfo, GitHubUser, GitHubOrganization, GitHubRepository } from '../../models/github.models';
import { UserProfileService } from '../../services/user-profile.service';
import { UserSettingsService } from '../../services/user-settings-service';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';
import { GitHubAuthService } from '../../services/github-auth.service';
import { GitHubApiService } from '../../services/github-api.service';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { ActivatedRoute } from '@angular/router';
import { UserSettingsEditorComponent, NewSetting } from '../user-settings-editor/user-settings-editor.component';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatListModule,
    UserSettingsEditorComponent
  ]
})
export class UserProfileComponent implements OnInit, OnDestroy {
  // User profile data (view-only)
  userProfile: AdoUserProfile | null = null;
  userEmail: string = '';
  userName: string = '';
  userRoles: string[] = [];

  // Team memberships
  userTeams: string[] = [];
  userGroups: string[] = [];
  carvanaADGroups: string[] = [];
  teamsLoadError: boolean = false;
  organization: string = 'CVNA-Wholesale'; // Default organization

  // User settings (editable)
  userSettings: UserSettings | null = null;
  saveSuccess = false;
  saveError = false;

  // Authentication status
  isAuthenticated = false;

  // GitHub integration - separate properties for connection info and data
  githubConnectionInfo: GitHubConnectionInfo = {
    isConnected: false,
    lastSync: null,
    error: null
  };
  githubUser: GitHubUser | null = null;
  githubOrganizations: GitHubOrganization[] = [];
  githubRepositories: GitHubRepository[] = [];
  githubLoading = false;

  // For cleanup on destroy
  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly userProfileService: UserProfileService,
    private readonly userSettingsService: UserSettingsService,
    public readonly loadingService: LoadingService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly githubAuth: GitHubAuthService,
    private readonly githubApi: GitHubApiService
  ) {}

  ngOnInit(): void {
    this.userEmail = this.userProfileService.getActiveAccount()?.username || '';
    console.log('[UserProfileComponent] Initialized with user email:', this.userEmail);
    this.userName = this.userProfileService.getActiveAccount()?.name || '';

    // Get the organization from the route params
    this.route.params.subscribe(params => {
      this.organization = params['org'] || 'CVNA-Wholesale';
      console.log('[UserProfileComponent] Organization set from route params:', this.organization);
      this.loadUserSettings();
      this.loadTeamMemberships();
    });

    // Monitor authentication status
    this.userProfileService.monitorAuthenticationStatus(this, this.destroy$);

    // Load user profile data (view-only)
    this.loadUserProfile();

    // Subscribe to user name and roles
    this.userProfileService.getUserName()
      .pipe(takeUntil(this.destroy$))
      .subscribe(name => this.userName = name);

    this.userProfileService.getUserRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe(roles => this.userRoles = roles);

    this.userProfileService.getUserEmail()
      .pipe(takeUntil(this.destroy$))
      .subscribe(email => this.userEmail = email);

    // Subscribe to GitHub connection info
    this.githubAuth.connectionInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(info => {
        this.githubConnectionInfo = info;
      });

    // Subscribe to GitHub user data
    this.githubApi.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.githubUser = user;
      });

    this.githubApi.organizations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgs => {
        this.githubOrganizations = orgs;
      });

    this.githubApi.repositories$
      .pipe(takeUntil(this.destroy$))
      .subscribe(repos => {
        this.githubRepositories = repos;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserProfile(): void {
    this.loadingService.show('userProfile');
    this.userProfileService.getCurrentProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          this.userProfile = profile;
          this.loadingService.hide('userProfile');
        },
        error: (err) => {
          console.error('Error loading user profile:', err);
          this.loadingService.hide('userProfile');
        }
      });
  }

  private loadUserSettings(): void {
    this.loadingService.show('userSettings');
    this.userProfileService.getUserSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.userSettings = settings;
          this.loadingService.hide('userSettings');
        },
        error: (err) => {
          console.error('Error loading user settings:', err);
          this.loadingService.hide('userSettings');
          this.toast.error('Failed to load user settings');
        }
      });
  }

  // Handle save settings event from child component
  onSaveSettings(settings: {[key: string]: any}): void {
    if (!this.userSettings) return;

    this.loadingService.show('saveSettings');
    this.saveSuccess = false;
    this.saveError = false;

    // Update settings object with new values
    this.userSettings.settings = settings;

    // Save settings
    this.userSettingsService.saveNow()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadingService.hide('saveSettings');
          this.saveSuccess = true;
          this.toast.success('Settings saved successfully');
        },
        error: (err) => {
          console.error('Error saving settings:', err);
          this.loadingService.hide('saveSettings');
          this.saveError = true;
          this.toast.error('Error saving settings');
        }
      });
  }

  // Handle add setting event from child component
  onAddSetting(newSetting: NewSetting): void {
    if (!this.userSettings) return;

    // Create the parsed value based on the selected type
    const parsedValue = this.parseSettingValue(newSetting.value, newSetting.type);

    // Add to settings object
    this.userSettings.setSetting(newSetting.key, parsedValue);

    // No need to create a new object, just trigger change detection
    // by updating a property that doesn't affect the instance methods
    this.userSettings = Object.assign(
      Object.create(Object.getPrototypeOf(this.userSettings)),
      this.userSettings
    );

    // Show feedback
    this.toast.notification(`Added setting: ${newSetting.key}`);
  }

  // Handle remove setting event from child component
  onRemoveSetting(key: string): void {
    if (!this.userSettings) return;

    this.userSettings.removeSetting(key);

    // No need to create a new object, just trigger change detection
    // by updating a property that doesn't affect the instance methods
    this.userSettings = Object.assign(
      Object.create(Object.getPrototypeOf(this.userSettings)),
      this.userSettings
    );

    // Show feedback with option to undo
    this.toast.info(`Removed setting: ${key}`, 'Undo');
    // Note: We can't restore the "Undo" functionality with ToastService as it doesn't support onAction
    // For full undo capability, we would need to implement a custom solution
  }

  // Helper to parse setting values based on type
  private parseSettingValue(value: string, type: string): any {
    switch (type) {
      case 'number':
        return Number(value);
      case 'boolean':
        return value.toLowerCase() === 'true';
      case 'object':
      case 'array':
        try {
          return JSON.parse(value);
        } catch (e) {
          console.error('Error parsing JSON:', e);
          return type === 'array' ? [] : {};
        }
      default:
        return value;
    }
  }

  loadTeamMemberships(): void {
    this.loadingService.show('teamMemberships');
    this.loadMembershipsWithEmail();
  }

  private loadMembershipsWithEmail(): void {
    if (!this.userEmail) {
      console.warn('No user email available, cannot load team memberships');
      this.loadingService.hide('teamMemberships');
      return;
    }

    this.userProfileService.getUserFullMembership(this.organization, this.userEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (membership) => {
          this.userTeams = membership.teams || [];
          this.userGroups = membership.groups || [];
          this.carvanaADGroups = membership.adGroups || [];
          this.teamsLoadError = false;
          this.loadingService.hide('teamMemberships');
        },
        error: (err: Error) => {
          console.error('Error loading team memberships:', err);
          this.teamsLoadError = true;
          this.loadingService.hide('teamMemberships');
        }
      });
  }

  // GitHub Integration Methods

  /**
   * Connects to GitHub using OAuth
   */
  connectGitHub(): void {
    this.githubLoading = true;
    this.githubAuth.loginWithGitHub()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // OAuth flow initiated, loading will be set to false by connection state changes
          // or when the user returns from the OAuth callback
        },
        error: (error: any) => {
          console.error('Error connecting to GitHub:', error);
          this.githubLoading = false;
        }
      });
  }

  /**
   * Disconnects from GitHub
   */
  disconnectGitHub(): void {
    this.githubAuth.logout();
    this.toast.notification('GitHub account disconnected');
  }

  /**
   * Refreshes GitHub user data
   */
  refreshGitHubData(): void {
    if (!this.githubConnectionInfo.isConnected) {
      return;
    }

    this.githubLoading = true;
    this.githubApi.refreshUserData(true) // Force refresh to bypass cache
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.githubLoading = false;
          this.toast.notification('GitHub data refreshed successfully');
        },
        error: (error) => {
          console.error('Error refreshing GitHub data:', error);
          this.toast.error('Failed to refresh GitHub data');
          this.githubLoading = false;
        }
      });
  }

  /**
   * Gets the display name for GitHub user
   */
  getGitHubDisplayName(): string {
    if (!this.githubUser) return '';
    return this.githubUser.name || this.githubUser.login || 'Unknown User';
  }

  /**
   * Gets the formatted last sync time
   */
  getLastSyncTime(): string {
    if (!this.githubConnectionInfo.lastSync) {
      return 'Never';
    }
    return this.githubConnectionInfo.lastSync.toLocaleString();
  }

}
